import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, Eye, Trash2, Calendar, ChevronLeft, ChevronRight, X, RotateCcw, Building2, Users, CheckCircle2 } from 'lucide-react';
import { departments, monthNames } from '../leaves/constants';
import { getStatusColor, calculateDuration, formatDate, calculateBusinessDays } from '../leaves/helpers';
import { getAllLeaves, approveLeave, rejectLeave, getMyLeaves, deleteLeave } from '../../services/leavesAPIs';
import { useOffices } from '../../offices';
import { Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, useTheme, Popover, Box, Chip, IconButton, Tooltip } from '@mui/material';
import { IconFileUpload, IconChecks } from '@tabler/icons-react';
import jsPDF from 'jspdf';

const getSubmissionAge = (createdAt) => {
  if (!createdAt) {
    return {
      label: '—',
      color: 'default',
      hint: 'Submission date not available'
    };
  }

  const created = new Date(createdAt);
  if (isNaN(created.getTime())) {
    return {
      label: '—',
      color: 'default',
      hint: 'Submission date not available'
    };
  }

  const now = new Date();
  const createdDay = new Date(created.getFullYear(), created.getMonth(), created.getDate());
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.max(0, Math.floor((nowDay - createdDay) / (24 * 60 * 60 * 1000)));

  if (days === 0) {
    return {
      label: 'Today',
      color: 'success',
      hint: `Submitted today (${formatDate(createdAt)})`
    };
  }
  if (days === 1) {
    return {
      label: '1 day ago',
      color: 'success',
      hint: `Submitted 1 day ago (${formatDate(createdAt)})`
    };
  }
  if (days <= 2) {
    return {
      label: `${days} days ago`,
      color: 'success',
      hint: `Submitted ${days} days ago (${formatDate(createdAt)})`
    };
  }
  if (days <= 7) {
    return {
      label: `${days} days ago`,
      color: 'warning',
      hint: `Submitted ${days} days ago (${formatDate(createdAt)})`
    };
  }
  return {
    label: `${days} days ago`,
    color: 'error',
    hint: `Older request: ${days} days ago (${formatDate(createdAt)})`
  };
};

const Requests = ({
  leavesData,
  filters,
  setFilters,
  setSelectedLeave,
  setLeavesData,
  IsEmployee,
  fetchData,
  setSelectedUpload,
  req = false,
  unpaid = false,
}) => {
  const alertRef = useRef(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [selectedLeaveId, setSelectedLeaveId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [Requests, setRequests] = useState([]);
  const [openMismatchDialog, setOpenMismatchDialog] = useState(false);
  const [mismatchData, setMismatchData] = useState(null);

  const { offices } = useOffices();

  const leaveTypeLabels = {
    SICK_LEAVE: 'Sick Leave',
    ANNUAL_LEAVE: 'Annual Leave',
    UNPAID_LEAVE: 'Unpaid Leave',
    MATERNITY_LEAVE: 'Maternity Leave',
  };

  const getApiErrorMessage = (error, fallbackMessage) => {
    const responseData = error?.response?.data;
    if (typeof responseData === 'string' && responseData.trim()) {
      return responseData;
    }
    if (responseData?.message) {
      return responseData.message;
    }
    if (responseData?.error) {
      return responseData.error;
    }
    return error?.message || fallbackMessage;
  };

  // Sync with external data
  useEffect(() => {
    setRequests(leavesData || []);
  }, [leavesData]);

  const toLocalDateString = (d) => {
    if (!d || isNaN(new Date(d).getTime())) return '';
    const dateObj = new Date(d);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get unique years from leave requests
  const availableYears = useMemo(() => {
    const years = new Set();
    const currentYear = new Date().getFullYear();
    years.add(currentYear);
    years.add(currentYear - 1);
    years.add(currentYear + 1);

    Requests.forEach(leave => {
      if (leave.startDate) {
        const y = new Date(leave.startDate).getFullYear();
        if (!isNaN(y)) years.add(y);
      }
    });

    return Array.from(years).sort((a, b) => b - a);
  }, [Requests]);

  const getUniqueYears = () => availableYears;

  // Calendar Filter States & Logic
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
  const [rangeSelectingStart, setRangeSelectingStart] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);

  const getInitialYear = () => {
    if (filters.startDate) {
      const d = new Date(filters.startDate);
      if (!isNaN(d.getTime())) return d.getFullYear();
    }
    if (filters.year) return parseInt(filters.year);
    return new Date().getFullYear();
  };

  const getInitialMonth = () => {
    if (filters.startDate) {
      const d = new Date(filters.startDate);
      if (!isNaN(d.getTime())) return d.getMonth();
    }
    if (filters.month) return parseInt(filters.month) - 1;
    return new Date().getMonth();
  };

  const [viewYear, setViewYear] = useState(getInitialYear);
  const [viewMonth, setViewMonth] = useState(getInitialMonth); // 0 - 11

  const formatDisplayDate = (dateVal) => {
    if (!dateVal) return '';
    if (typeof dateVal === 'string') {
      const parts = dateVal.split('-');
      if (parts.length === 3) {
        return `${parts[2].padStart(2, '0')}/${parts[1].padStart(2, '0')}/${parts[0]}`;
      }
    }
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getDateFilterLabel = () => {
    if (filters.startDate && filters.endDate) {
      if (filters.startDate === filters.endDate) {
        return formatDisplayDate(filters.startDate);
      }
      return `${formatDisplayDate(filters.startDate)} - ${formatDisplayDate(filters.endDate)}`;
    }
    if (filters.startDate) {
      return `From ${formatDisplayDate(filters.startDate)}`;
    }
    if (filters.endDate) {
      return `Until ${formatDisplayDate(filters.endDate)}`;
    }
    if (filters.month && filters.year) {
      return `${monthNames[filters.month - 1]} ${filters.year}`;
    }
    if (filters.month) {
      return monthNames[filters.month - 1];
    }
    if (filters.year) {
      return `Year ${filters.year}`;
    }
    return 'All Dates';
  };

  const hasActiveDateFilter = Boolean(filters.startDate || filters.endDate || filters.month || filters.year);

  const handleOpenCalendar = (e) => {
    setCalendarAnchorEl(e.currentTarget);
    if (filters.startDate) {
      const d = new Date(filters.startDate);
      if (!isNaN(d.getTime())) {
        setViewYear(d.getFullYear());
        setViewMonth(d.getMonth());
      }
    } else if (filters.year) {
      setViewYear(parseInt(filters.year));
      if (filters.month) setViewMonth(parseInt(filters.month) - 1);
    }
    setRangeSelectingStart(null);
    setHoverDate(null);
  };

  const handleCloseCalendar = () => {
    if (rangeSelectingStart) {
      const d = new Date(rangeSelectingStart);
      setFilters(prev => ({
        ...prev,
        startDate: rangeSelectingStart,
        endDate: rangeSelectingStart,
        year: !isNaN(d.getTime()) ? d.getFullYear() : prev.year,
        month: !isNaN(d.getTime()) ? d.getMonth() + 1 : prev.month
      }));
      setRangeSelectingStart(null);
    }
    setHoverDate(null);
    setCalendarAnchorEl(null);
  };

  const handleClearDates = () => {
    setFilters(prev => ({
      ...prev,
      startDate: null,
      endDate: null,
      year: null,
      month: null
    }));
    setRangeSelectingStart(null);
    setHoverDate(null);
    setCalendarAnchorEl(null);
  };

  const isAnyFilterActive = Boolean(
    filters.office ||
    filters.type ||
    filters.status ||
    filters.department ||
    filters.search ||
    filters.startDate ||
    filters.endDate ||
    filters.year ||
    filters.month
  );

  const handleResetAllFilters = () => {
    setFilters(prev => ({
      ...prev,
      office: '',
      type: '',
      status: '',
      department: '',
      year: null,
      month: null,
      startDate: null,
      endDate: null,
      search: '',
    }));
    const now = new Date();
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
    setRangeSelectingStart(null);
    setHoverDate(null);
    setCalendarAnchorEl(null);
  };

  const handleSelectDate = (dateStr) => {
    if (!rangeSelectingStart) {
      setRangeSelectingStart(dateStr);
    } else {
      let start = rangeSelectingStart;
      let end = dateStr;
      if (end < start) {
        const tmp = start;
        start = end;
        end = tmp;
      }
      const sDate = new Date(start);
      const eDate = new Date(end);
      const isSameMonth = sDate.getFullYear() === eDate.getFullYear() && sDate.getMonth() === eDate.getMonth();

      setFilters(prev => ({
        ...prev,
        startDate: start,
        endDate: end,
        year: sDate.getFullYear(),
        month: isSameMonth ? sDate.getMonth() + 1 : null
      }));
      setRangeSelectingStart(null);
      setHoverDate(null);
      setCalendarAnchorEl(null);
    }
  };

  const handlePreset = (presetType) => {
    const today = new Date();
    let startStr = null;
    let endStr = null;
    let yearVal = null;
    let monthVal = null;

    if (presetType === 'today') {
      startStr = toLocalDateString(today);
      endStr = startStr;
      yearVal = today.getFullYear();
      monthVal = today.getMonth() + 1;
    } else if (presetType === 'thisMonth') {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      startStr = toLocalDateString(first);
      endStr = toLocalDateString(last);
      yearVal = today.getFullYear();
      monthVal = today.getMonth() + 1;
    } else if (presetType === 'lastMonth') {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      const ref = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      startStr = toLocalDateString(first);
      endStr = toLocalDateString(last);
      yearVal = ref.getFullYear();
      monthVal = ref.getMonth() + 1;
    } else if (presetType === 'thisYear') {
      const first = new Date(today.getFullYear(), 0, 1);
      const last = new Date(today.getFullYear(), 11, 31);
      startStr = toLocalDateString(first);
      endStr = toLocalDateString(last);
      yearVal = today.getFullYear();
      monthVal = null;
    } else if (presetType === 'viewMonth') {
      const first = new Date(viewYear, viewMonth, 1);
      const last = new Date(viewYear, viewMonth + 1, 0);
      startStr = toLocalDateString(first);
      endStr = toLocalDateString(last);
      yearVal = viewYear;
      monthVal = viewMonth + 1;
    }

    setFilters(prev => ({
      ...prev,
      startDate: startStr,
      endDate: endStr,
      year: yearVal,
      month: monthVal
    }));
    setRangeSelectingStart(null);
    setHoverDate(null);
    setCalendarAnchorEl(null);
  };

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(prev => prev - 1);
    } else {
      setViewMonth(prev => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(prev => prev + 1);
    } else {
      setViewMonth(prev => prev + 1);
    }
  };

  const calendarDays = useMemo(() => {
    const days = [];
    const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
    const lastDayOfMonth = new Date(viewYear, viewMonth + 1, 0);
    const numDays = lastDayOfMonth.getDate();

    // Monday=0 ... Sunday=6
    const startDayOfWeek = (firstDayOfMonth.getDay() + 6) % 7;

    const prevMonthLastDay = new Date(viewYear, viewMonth, 0).getDate();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const d = new Date(viewYear, viewMonth - 1, prevMonthLastDay - i);
      days.push({
        dateStr: toLocalDateString(d),
        dayNum: prevMonthLastDay - i,
        isCurrentMonth: false,
      });
    }

    for (let d = 1; d <= numDays; d++) {
      const curDate = new Date(viewYear, viewMonth, d);
      days.push({
        dateStr: toLocalDateString(curDate),
        dayNum: d,
        isCurrentMonth: true,
      });
    }

    const remaining = (7 - (days.length % 7)) % 7;
    for (let d = 1; d <= remaining; d++) {
      const nextDate = new Date(viewYear, viewMonth + 1, d);
      days.push({
        dateStr: toLocalDateString(nextDate),
        dayNum: d,
        isCurrentMonth: false,
      });
    }

    return days;
  }, [viewYear, viewMonth]);

  const todayStr = useMemo(() => toLocalDateString(new Date()), []);

  const getDayStatus = (dateStr) => {
    let start = rangeSelectingStart || filters.startDate;
    let end = rangeSelectingStart ? hoverDate : filters.endDate;

    if (start && end && end < start) {
      const tmp = start;
      start = end;
      end = tmp;
    }

    const isStart = Boolean(start && dateStr === start);
    const isEnd = Boolean(end && dateStr === end);
    const isInRange = Boolean(start && end && dateStr > start && dateStr < end);

    return { isStart, isEnd, isInRange };
  };

  const handleOpenRejectDialog = (leaveId) => {
    setSelectedLeaveId(leaveId);
    setRejectionReason('');
    setOpenRejectDialog(true);
  };

  const handleCloseRejectDialog = () => {
    setOpenRejectDialog(false);
    setSelectedLeaveId(null);
    setRejectionReason('');
  };

  const handleCloseMismatchDialog = () => {
    setOpenMismatchDialog(false);
    setMismatchData(null);
  };

  const getEmployeeAnnualLeaves = (leaveId) => {
    const targetLeave = Requests.find(item => item.requestId === leaveId);
    if (!targetLeave) return [];

    const targetEmployeeId = targetLeave.employee?.employeeId || targetLeave.employeeId || '';

    return Requests
      .filter(item => {
        const itemEmployeeId = item.employee?.employeeId || item.employeeId || '';
        const itemStatus = (item.status || '').toUpperCase();
        return item.type === 'ANNUAL_LEAVE' && itemEmployeeId === targetEmployeeId && itemStatus === 'PENDING';
      })
      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  };

  const exportMismatchPdf = () => {
    if (!mismatchData) return;

    const doc = new jsPDF('p', 'mm', 'a4');
    let y = 15;
    const requestedDays = (mismatchData.annualLeaves || []).reduce(
      (sum, leave) => sum + calculateBusinessDays(leave.startDate, leave.endDate),
      0
    );

    const writeLine = (text, spacing = 7) => {
      const lines = doc.splitTextToSize(text, 180);
      doc.text(lines, 15, y);
      y += (lines.length * 5) + (spacing - 5);
      if (y > 275) {
        doc.addPage();
        y = 15;
      }
    };

    const writeSummaryRow = (label, value, bg = [245, 247, 250]) => {
      doc.setFillColor(bg[0], bg[1], bg[2]);
      doc.roundedRect(15, y - 5, 180, 10, 2, 2, 'F');
      doc.setFontSize(10);
      doc.setTextColor(55, 65, 81);
      doc.text(`${label}:`, 18, y + 1);
      doc.setTextColor(17, 24, 39);
      doc.setFont(undefined, 'bold');
      doc.text(String(value), 75, y + 1);
      doc.setFont(undefined, 'normal');
      y += 13;
      if (y > 275) {
        doc.addPage();
        y = 15;
      }
    };

    doc.setFillColor(14, 116, 144);
    doc.roundedRect(12, 8, 186, 20, 3, 3, 'F');
    doc.setFontSize(15);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    doc.text('Pending Balance Mismatch', 16, 17);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text('Vacation Planning Update Notice', 16, 23);
    y = 35;

    doc.setTextColor(17, 24, 39);
    doc.setFontSize(11);
    writeSummaryRow('Employee', mismatchData.employeeName || 'Unknown', [239, 246, 255]);
    // writeSummaryRow('Employee ID', mismatchData.employeeId || 'N/A');
    writeSummaryRow('Difference Days to Reduce', mismatchData.differenceDays ?? 'N/A', [255, 237, 213]);
    writeSummaryRow('Requested Days (Pending)', requestedDays, [220, 252, 231]);

    y += 2;
    doc.setDrawColor(203, 213, 225);
    doc.line(15, y, 195, y);
    y += 6;

    doc.setTextColor(55, 65, 81);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    // doc.text('Backend Message', 15, y);
    y += 5;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(31, 41, 55);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(15, y - 4, 180, 14, 2, 2, 'F');
    writeLine('Action Required: The annual leave plan must be decreased by the difference days. Please select one or more periods below and update them.');
    writeLine(`Message: ${mismatchData.message || 'N/A'}`);

    y += 2;
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(17, 24, 39);
    doc.text('Employee Annual Leave Requests', 15, y);
    y += 8;

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    if (!mismatchData.annualLeaves?.length) {
      writeLine('No annual leave requests found for this employee.');
    } else {
      doc.setFillColor(59, 130, 246);
      doc.rect(15, y - 5, 180, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.text('Pending Requests Included Below', 18, y);
      y += 8;
      doc.setTextColor(17, 24, 39);

      mismatchData.annualLeaves.forEach((leave, index) => {
        const duration = calculateBusinessDays(leave.startDate, leave.endDate);
        const rowColor = index % 2 === 0 ? [248, 250, 252] : [255, 255, 255];
        doc.setFillColor(rowColor[0], rowColor[1], rowColor[2]);
        doc.rect(15, y - 4, 180, 9, 'F');
        writeLine(
          `${index + 1}. Annual leave Request | ${formatDate(leave.startDate)} to ${formatDate(leave.endDate)} | ${duration} day${duration === 1 ? '' : 's'} | Status: ${leave.status || 'N/A'}`,
          6
        );
      });
    }

    const safeEmployee = (mismatchData.employeeName || 'employee').replace(/\s+/g, '_');
    const safeDate = new Date().toISOString().split('T')[0];
    doc.save(`pending-balance-mismatch-${safeEmployee}-${safeDate}.pdf`);
  };

  const handleStatusChange = async (leaveId, newStatus, reason = '') => {
    try {
      let updatedLeave;
      if (newStatus === 'approved') {
        updatedLeave = await approveLeave(leaveId);
      } else if (newStatus === 'rejected') {
        updatedLeave = await rejectLeave(leaveId, { reason });
        setSuccessMessage(`Leave rejected successfully. Reason: ${reason}`);
      }
      setSuccessMessage(`Leave ${newStatus.toLowerCase()} successfully.`);
      setErrorMessage('');
      if (alertRef.current) {
        alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setTimeout(() => setSuccessMessage(''), 5000);
      await fetchData();
    } catch (error) {
      console.log("backend response:", error?.response);
      const responseData = error?.response?.data || {};
      const isPendingBalanceMismatch =
        responseData?.exceptionType === 'PendingBalanceMismatchException' ||
        responseData?.error === 'Pending Balance Mismatch';

      if (newStatus === 'approved' && isPendingBalanceMismatch) {
        const targetLeave = Requests.find(item => item.requestId === leaveId);
        const employeeName = `${targetLeave?.employee?.firstName || ''} ${targetLeave?.employee?.lastName || ''}`.trim() || 'Unknown';
        const employeeId = targetLeave?.employee?.employeeId || targetLeave?.employeeId || '';
        const annualLeaves = getEmployeeAnnualLeaves(leaveId);

        setMismatchData({
          ...responseData,
          employeeName,
          employeeId,
          annualLeaves
        });
        setOpenMismatchDialog(true);
        setSuccessMessage('');
        setErrorMessage('');
        return;
      }

      const errorMsg = getApiErrorMessage(error, `Failed to ${newStatus.toLowerCase()} leave.`);
      setErrorMessage(errorMsg.includes('contract')
        ? 'Cannot approve: Employee has no contract. Please add one.'
        : errorMsg
      );
      setSuccessMessage('');
      if (alertRef.current) {
        alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const handleRejectSubmit = () => {
    if (rejectionReason.trim()) {
      handleStatusChange(selectedLeaveId, 'rejected', rejectionReason);
      handleCloseRejectDialog();
    } else {
      setErrorMessage('Please provide a rejection reason.');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const handleDeleteLeave = async (leaveId) => {
    if (window.confirm('Are you sure you want to delete this leave request?')) {
      try {
        await deleteLeave(leaveId);
        setSuccessMessage('Leave request deleted successfully.');
        setErrorMessage('');
        if (alertRef.current) {
          alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        setTimeout(() => setSuccessMessage(''), 5000);
        await fetchData();
      } catch (error) {
        const errorMsg = getApiErrorMessage(error, 'Failed to delete leave request.');
        setErrorMessage(errorMsg);
        setSuccessMessage('');
        if (alertRef.current) {
          alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        setTimeout(() => setErrorMessage(''), 5000);
      }
    }
  };

  // Filter Logic
  const filteredData = Requests.filter(leave => {
    const leaveDate = leave.startDate && !isNaN(new Date(leave.startDate).getTime()) ? new Date(leave.startDate) : null;
    const leaveMonth = leaveDate ? leaveDate.getMonth() + 1 : null;
    const leaveYear = leaveDate ? leaveDate.getFullYear() : null;

    const typeMatch = !filters.type || leave.type === filters.type;
    const statusMatch = !filters.status || leave.status?.toLowerCase() === filters.status?.toLowerCase();
    const deptMatch = !filters.department || (leave.departmentName || '').toLowerCase() === filters.department.toLowerCase();
    const officeMatch = !filters.office || (leave.officeName || '').toLowerCase() === filters.office.toLowerCase();

    // Date / Range match (Calendar filter)
    const dateRangeMatch = (() => {
      if (!filters.startDate && !filters.endDate) {
        const monthMatch = !filters.month || (leaveMonth && leaveMonth === parseInt(filters.month));
        const yearMatch = !filters.year || (leaveYear && leaveYear === parseInt(filters.year));
        return monthMatch && yearMatch;
      }
      if (!leaveDate) return false;
      const leaveStartStr = leave.startDate;
      const leaveEndStr = leave.endDate || leave.startDate;
      if (filters.startDate && filters.endDate) {
        return leaveStartStr <= filters.endDate && leaveEndStr >= filters.startDate;
      }
      if (filters.startDate) {
        return leaveEndStr >= filters.startDate;
      }
      if (filters.endDate) {
        return leaveStartStr <= filters.endDate;
      }
      return true;
    })();

    const searchMatch = (() => {
      const rawQuery = (filters.search || '').toLowerCase().trim();
      if (!rawQuery) return true;

      const queryTokens = rawQuery.split(/\s+/).filter(Boolean);
      const first = (leave.employeeFirstName || '').toLowerCase().trim();
      const last = (leave.employeeLastName || '').toLowerCase().trim();
      const full = `${first} ${last}`.replace(/\s+/g, ' ').trim();
      const reverseFull = `${last} ${first}`.replace(/\s+/g, ' ').trim();
      const employeeId = (leave.employeeId || '').toLowerCase();

      return (
        employeeId.includes(rawQuery) ||
        full.includes(rawQuery) ||
        reverseFull.includes(rawQuery) ||
        queryTokens.every(token => first.includes(token) || last.includes(token) || full.includes(token))
      );
    })();

    return typeMatch && statusMatch && deptMatch && officeMatch && dateRangeMatch && searchMatch;
  }).sort((a, b) => {
    // Sort by status: pending first, then others
    const statusOrder = { 'PENDING': 0, 'APPROVED': 1, 'REJECTED': 2, 'CANCELLED': 3 };
    const statusA = (a.status || '').toUpperCase();
    const statusB = (b.status || '').toUpperCase();
    const orderA = statusOrder[statusA] !== undefined ? statusOrder[statusA] : 4;
    const orderB = statusOrder[statusB] !== undefined ? statusOrder[statusB] : 4;

    if (orderA !== orderB) {
      return orderA - orderB;
    }

    // If same status, sort by date (newest first)
    return new Date(b.startDate) - new Date(a.startDate);
  });

  const mismatchRequestedDays = (mismatchData?.annualLeaves || []).reduce(
    (sum, leave) => sum + calculateBusinessDays(leave.startDate, leave.endDate),
    0
  );

  return (
    <div className="Leaves-requests-container">
      <div className="Leaves-requests-header">
        {/* <h3 className="Leaves-requests-title">Leave Requests</h3> */}

        <div ref={alertRef}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2, maxWidth: 800 }}>
              {errorMessage}
            </Alert>
          )}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2, maxWidth: 800 }}>
              {successMessage}
            </Alert>
          )}
        </div>

        <div className="Leaves-requests-filters">
          {/* Office Filter */}
          {!IsEmployee && (
            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <Building2
                size={18}
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#1976d2',
                  pointerEvents: 'none',
                  zIndex: 1,
                  flexShrink: 0
                }}
              />
              <select
                value={filters.office}
                onChange={(e) => setFilters(prev => ({ ...prev, office: e.target.value }))}
                className="Leaves-filter-select"
                style={{
                  paddingLeft: '38px',
                  borderColor: filters.office ? '#1976d2' : undefined,
                  fontWeight: filters.office ? 600 : 500,
                }}
              >
                <option value="">All Offices</option>
                {offices.map(office => (
                  <option key={office.id} value={office.name}>{office.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Department Filter */}
          {!IsEmployee && (
            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <Users
                size={18}
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#1976d2',
                  pointerEvents: 'none',
                  zIndex: 1,
                  flexShrink: 0
                }}
              />
              <select
                value={filters.department}
                onChange={(e) => setFilters(prev => ({ ...prev, department: e.target.value }))}
                className="Leaves-filter-select"
                style={{
                  paddingLeft: '38px',
                  borderColor: filters.department ? '#1976d2' : undefined,
                  fontWeight: filters.department ? 600 : 500,
                }}
              >
                <option value="">All Departments</option>
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
          )}

          {/* Status Filter */}
          {req && (
            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <CheckCircle2
                size={18}
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#1976d2',
                  pointerEvents: 'none',
                  zIndex: 1,
                  flexShrink: 0
                }}
              />
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="Leaves-filter-select"
                style={{
                  paddingLeft: '38px',
                  borderColor: filters.status ? '#1976d2' : undefined,
                  fontWeight: filters.status ? 600 : 500,
                }}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          )}
          {/* Merged Date Filter (Year, Month, Start & End Date via Calendar) */}
          <div
            className="Leaves-filter-select"
            onClick={handleOpenCalendar}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              background: hasActiveDateFilter ? 'var(--oc-blue-0)' : 'white',
              borderColor: hasActiveDateFilter ? '#1976d2' : undefined,
              color: hasActiveDateFilter ? '#1976d2' : 'var(--oc-gray-7)',
              fontWeight: hasActiveDateFilter ? 600 : 500,
              minWidth: '170px',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
              <Calendar size={18} style={{ color: '#1976d2', flexShrink: 0 }} />
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {getDateFilterLabel()}
              </span>
            </div>
            {hasActiveDateFilter ? (
              <X
                size={16}
                style={{ cursor: 'pointer', flexShrink: 0, color: '#e03131' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearDates();
                }}
                title="Clear date filter"
              />
            ) : (
              <span style={{ fontSize: '10px', color: '#868e96', marginLeft: '4px' }}>▼</span>
            )}
          </div>

          <Popover
            open={Boolean(calendarAnchorEl)}
            anchorEl={calendarAnchorEl}
            onClose={handleCloseCalendar}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            slotProps={{
              paper: {
                sx: {
                  p: 2,
                  mt: 1,
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15), 0 8px 10px -6px rgba(0,0,0,0.1)',
                  width: 320,
                  maxWidth: '95vw',
                }
              }
            }}
          >
            {/* Quick Presets */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Chip
                label="All Dates"
                size="small"
                onClick={handleClearDates}
                clickable
                color={!hasActiveDateFilter ? 'primary' : 'default'}
                variant={!hasActiveDateFilter ? 'filled' : 'outlined'}
                sx={{ fontSize: '0.75rem', height: '24px' }}
              />
              <Chip
                label="Today"
                size="small"
                onClick={() => handlePreset('today')}
                clickable
                variant="outlined"
                sx={{ fontSize: '0.75rem', height: '24px' }}
              />
              <Chip
                label="This Month"
                size="small"
                onClick={() => handlePreset('thisMonth')}
                clickable
                variant="outlined"
                sx={{ fontSize: '0.75rem', height: '24px' }}
              />
              <Chip
                label="Last Month"
                size="small"
                onClick={() => handlePreset('lastMonth')}
                clickable
                variant="outlined"
                sx={{ fontSize: '0.75rem', height: '24px' }}
              />
              <Chip
                label="This Year"
                size="small"
                onClick={() => handlePreset('thisYear')}
                clickable
                variant="outlined"
                sx={{ fontSize: '0.75rem', height: '24px' }}
              />
            </Box>

            {/* Month / Year Navigator */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, width: '100%', boxSizing: 'border-box' }}>
              <IconButton size="small" onClick={handlePrevMonth} sx={{ p: 0.5, flexShrink: 0 }}>
                <ChevronLeft size={18} />
              </IconButton>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center', flexGrow: 1, px: 0.5 }}>
                <select
                  value={viewMonth}
                  onChange={(e) => setViewMonth(parseInt(e.target.value))}
                  style={{
                    padding: '4px 6px',
                    fontSize: '0.82rem',
                    height: '30px',
                    borderRadius: '4px',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    background: 'inherit',
                    color: 'inherit',
                    minWidth: 'unset',
                    width: '110px',
                    maxWidth: '115px',
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                  }}
                >
                  {monthNames.map((name, i) => (
                    <option key={name} value={i} style={{ color: '#000', background: '#fff' }}>{name}</option>
                  ))}
                </select>
                <select
                  value={viewYear}
                  onChange={(e) => setViewYear(parseInt(e.target.value))}
                  style={{
                    padding: '4px 6px',
                    fontSize: '0.82rem',
                    height: '30px',
                    borderRadius: '4px',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    background: 'inherit',
                    color: 'inherit',
                    minWidth: 'unset',
                    width: '75px',
                    maxWidth: '80px',
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                  }}
                >
                  {availableYears.map(y => (
                    <option key={y} value={y} style={{ color: '#000', background: '#fff' }}>{y}</option>
                  ))}
                </select>
              </Box>
              <IconButton size="small" onClick={handleNextMonth} sx={{ p: 0.5, flexShrink: 0 }}>
                <ChevronRight size={18} />
              </IconButton>
            </Box>

            {/* Quick button to select the whole viewed month */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <Button
                size="small"
                variant="text"
                onClick={() => handlePreset('viewMonth')}
                sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.2 }}
              >
                Select entire {monthNames[viewMonth]} {viewYear}
              </Button>
            </Box>

            {/* Day of week headers */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '2px',
                textAlign: 'center',
                marginBottom: '4px',
              }}
            >
              {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(dayName => (
                <div
                  key={dayName}
                  style={{
                    padding: '4px 0',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: '#64748b',
                    textTransform: 'uppercase',
                  }}
                >
                  {dayName}
                </div>
              ))}
              {calendarDays.map((item, idx) => {
                const { isStart, isEnd, isInRange } = getDayStatus(item.dateStr);
                const isToday = item.dateStr === todayStr;
                const isSelected = isStart || isEnd;

                let bg = 'transparent';
                let color = item.isCurrentMonth ? 'inherit' : 'rgba(100, 116, 139, 0.4)';
                let borderRadius = '6px';

                if (isSelected) {
                  bg = '#1976d2';
                  color = '#ffffff';
                } else if (isInRange) {
                  bg = 'rgba(25, 118, 210, 0.12)';
                  borderRadius = '0px';
                }

                return (
                  <div
                    key={`${item.dateStr}-${idx}`}
                    onClick={() => handleSelectDate(item.dateStr)}
                    onMouseEnter={() => rangeSelectingStart && setHoverDate(item.dateStr)}
                    style={{
                      padding: '6px 0',
                      cursor: 'pointer',
                      borderRadius,
                      backgroundColor: bg,
                      color,
                      fontSize: '0.85rem',
                      fontWeight: isSelected || isToday ? 600 : 400,
                      border: isToday && !isSelected ? '1px solid #1976d2' : '1px solid transparent',
                      transition: 'background-color 0.12s ease',
                      userSelect: 'none',
                    }}
                  >
                    {item.dayNum}
                  </div>
                );
              })}
            </div>

            {/* Footer with Reset and Done */}
            <Box
              sx={{
                mt: 1.5,
                pt: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Button
                size="small"
                color="inherit"
                onClick={handleClearDates}
                sx={{ textTransform: 'none', fontSize: '0.8rem' }}
              >
                Reset
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleCloseCalendar}
                sx={{ textTransform: 'none', fontSize: '0.8rem', px: 2 }}
              >
                Done
              </Button>
            </Box>
          </Popover>

          {/* Search */}
          {!IsEmployee && (
            <div className="Leaves-search-container">
              <Search className="Leaves-search-icon" size={18} />
              <input
                type="text"
                placeholder="Search employee..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="Leaves-search-input"
              />
            </div>
          )}

          {/* Reset Filters button */}
          <button
            type="button"
            onClick={handleResetAllFilters}
            title="Reset all filters"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1.5px solid',
              borderColor: isAnyFilterActive ? '#1976d2' : 'var(--oc-gray-3)',
              background: isAnyFilterActive ? 'var(--oc-blue-0)' : 'white',
              color: isAnyFilterActive ? '#1976d2' : 'var(--oc-gray-6)',
              cursor: isAnyFilterActive ? 'pointer' : 'default',
              fontSize: '13px',
              fontWeight: 600,
              transition: 'all 0.2s ease',
              opacity: isAnyFilterActive ? 1 : 0.6,
              height: '40px',
            }}
            disabled={!isAnyFilterActive}
          >
            <RotateCcw size={15} style={{ color: isAnyFilterActive ? '#1976d2' : 'var(--oc-gray-6)' }} />
            <span>Reset</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="Leaves-table-wrapper">
        <table className="Leaves-table">
          <thead className="Leaves-table-head">
            <tr>
              {!IsEmployee && <th className="Leaves-table-header col-priority-5">Employee</th>}
              {!IsEmployee && <th className="Leaves-table-header col-priority-1">Department</th>}
              {!IsEmployee && <th className="Leaves-table-header col-priority-1">Office</th>}
              {(req || IsEmployee) && <th className="Leaves-table-header col-priority-4">Leave Type</th>}
              <th className="Leaves-table-header col-priority-4">Start Date</th>
              <th className="Leaves-table-header col-priority-4">End Date</th>
              <th className="Leaves-table-header col-priority-2">Duration</th>
              <th className="Leaves-table-header col-priority-2">Hours</th>
              <th className="Leaves-table-header col-priority-3">Description</th>
              <th className="Leaves-table-header col-priority-3">Handled by</th>
              <th className="Leaves-table-header col-priority-3">Submission Age</th>
              {unpaid && <th className="Leaves-table-header col-priority-2">Justified</th>}
              {IsEmployee && <th className="Leaves-table-header col-priority-5">Status</th>}
              <th className="Leaves-table-header col-priority-5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan={IsEmployee ? 10 : 13} className="Leaves-table-cell" style={{ textAlign: 'center', fontStyle: 'italic', color: '#868e96' }}>
                  No leave requests found
                </td>
              </tr>
            ) : (
              filteredData.map(leave => (
                <tr key={leave.requestId} className="Leaves-table-row">
                  {/* Employee */}
                  {!IsEmployee && (
                    <td className="Leaves-table-cell col-priority-5">
                      <div className="Leaves-employee-info">
                        <p className="Leaves-employee-name">
                          {leave.employeeFirstName || 'Unknown'} {leave.employeeLastName || 'Unknown'}
                        </p>
                      </div>
                    </td>
                  )}

                  {/* Department */}
                  {!IsEmployee && (
                    <td className="Leaves-table-cell col-priority-1">{leave.departmentName || 'N/A'}</td>
                  )}

                  {/* Office */}
                  {!IsEmployee && (
                    <td className="Leaves-table-cell col-priority-1">{leave.officeName || 'N/A'}</td>
                  )}

                  {/* Leave Type */}
                  {(req || IsEmployee) && (
                    <td className="Leaves-table-cell col-priority-4">
                      <span className={`Leaves-leave-type ${leave.type === 'ANNUAL_LEAVE' ? 'Leaves-leave-vacation' :
                        leave.type === 'SICK_LEAVE' ? 'Leaves-leave-sick' :
                          leave.type === 'UNPAID_LEAVE' ? 'Leaves-leave-personal' :
                            'Leaves-leave-default'
                        }`}>
                        {leaveTypeLabels[leave.type] || leave.type}
                      </span>
                    </td>
                  )}

                  {/* Dates */}
                  <td className="Leaves-table-cell col-priority-4">{formatDate(leave.startDate)}</td>
                  <td className="Leaves-table-cell col-priority-4">to {formatDate(leave.endDate)}</td>

                  {/* Duration */}
                  <td className="Leaves-table-cell col-priority-2">
                    {leave.type === 'UNPAID_LEAVE'
                      ? `${leave.hours || (leave.days * 8)} hours`
                      : `${leave.days} ${leave.days === 1 ? 'day' : 'days'}`}
                  </td>

                  <td className="Leaves-table-cell col-priority-2">{leave.hours || (leave.days * 8)} </td>

                  {/* Justification */}
                  <td className="Leaves-table-cell col-priority-3" style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {leave.justification || '—'}
                  </td>

                  <td className="Leaves-table-cell col-priority-3">{leave.handledBy || '—'}</td>
                  <td className="Leaves-table-cell col-priority-3">
                    {(() => {
                      const rawDate = leave.createdAt || leave.submittedDate || leave.submittedAt || leave.submissionDate;
                      const age = getSubmissionAge(rawDate);
                      return (
                        <Tooltip title={age.hint} arrow>
                          <Chip
                            size="small"
                            label={age.label}
                            color={age.color}
                            variant={age.color === 'default' ? 'outlined' : 'filled'}
                            sx={{ fontWeight: 600, fontSize: '0.75rem', height: '24px' }}
                          />
                        </Tooltip>
                      );
                    })()}
                  </td>

                  {/* Justified (Unpaid) */}
                  {unpaid && (
                    <td className="Leaves-table-cell col-priority-2" style={{ textAlign: 'center' }}>
                      {leave.content ? (
                        <IconChecks size={20} color="#2b8a3e" title="Justified" />
                      ) : (
                        <span style={{ color: '#c92a2a' }} title="Not justified">—</span>
                      )}
                    </td>
                  )}

                  {/* Status (Employee View) */}
                  {IsEmployee && (
                    <td className="Leaves-table-cell col-priority-5">
                      <span className={`Leaves-status-badge ${getStatusColor(leave.status)}`}>
                        {leave.status}
                      </span>
                    </td>
                  )}

                  {/* Actions */}
                  <td className="Leaves-table-cell col-priority-5">
                    <div className="Leaves-actions">
                      <button
                        onClick={() => setSelectedLeave(leave.requestId)}
                        className="Leaves-btn Leaves-btn-primary"
                        title="View Details"
                      >
                        <Eye size={18} />
                      </button>

                      {/* Upload for Unpaid */}
                      {leave.type === 'UNPAID_LEAVE' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedUpload(leave);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 4,
                            color: '#1976d2'
                          }}
                          title="Upload Justification"
                        >
                          <IconFileUpload size={22} />
                        </button>
                      )}

                      {/* Approve / Reject (Admin) */}
                      {!IsEmployee && leave.status === 'pending' && (
                        <div className="Leaves-action-buttons" style={{ display: 'flex', gap: 6 }}>
                          <button
                            onClick={() => handleStatusChange(leave.requestId, 'approved')}
                            className="Leaves-btn Leaves-btn-save"
                            title="Approve"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => handleOpenRejectDialog(leave.requestId)}
                            className="Leaves-btn Leaves-btn-delete"
                            title="Reject"
                          >
                            ✗
                          </button>
                        </div>
                      )}

                      {/* Delete Button */}
                      {!IsEmployee && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteLeave(leave.requestId);
                          }}
                          className="Leaves-btn Leaves-btn-delete"
                          title="Delete Request"
                          style={{ marginLeft: 6 }}
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Reject Dialog */}
      <Dialog
        open={openRejectDialog}
        onClose={handleCloseRejectDialog}
        BackdropProps={{
          sx: {
            backdropFilter: 'blur(6px)',
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
          }
        }}
        PaperProps={{
          sx: {
            bgcolor: isDark ? '#1e293b' : 'background.paper',
            backgroundImage: 'none',
            boxShadow: 24,
          }
        }}
      >
        <DialogTitle sx={{ color: isDark ? 'white' : 'inherit' }}>Reject Leave Request</DialogTitle>
        <DialogContent sx={{ borderColor: isDark ? '#334155' : 'rgba(0,0,0,0.12)' }}>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for Rejection"
            type="text"
            fullWidth
            variant="outlined"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            multiline
            rows={2}
            InputProps={{ sx: { color: isDark ? 'white' : 'inherit' } }}
            InputLabelProps={{ sx: { color: isDark ? '#94a3b8' : 'inherit' } }}
            sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? '#475569' : undefined } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRejectDialog}>Cancel</Button>
          <Button onClick={handleRejectSubmit} color="error" variant="contained">
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openMismatchDialog}
        onClose={handleCloseMismatchDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: isDark ? '#1e293b' : 'background.paper',
            backgroundImage: 'none',
            boxShadow: 24,
          }
        }}
      >
        <DialogTitle sx={{ color: isDark ? 'white' : 'inherit' }}>
          Pending Balance Mismatch
        </DialogTitle>
        <DialogContent sx={{ borderColor: isDark ? '#334155' : 'rgba(0,0,0,0.12)' }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Pending leave plan is no longer aligned with available pending balance.
          </Alert>

          <div
            style={{
              marginBottom: 12,
              padding: '10px 12px',
              borderRadius: 8,
              background: isDark ? '#0f172a' : '#eff6ff',
              border: `1px solid ${isDark ? '#1e40af' : '#bfdbfe'}`,
              color: isDark ? '#dbeafe' : '#1e3a8a'
            }}
          >
            <strong>Employee:</strong> {mismatchData?.employeeName || 'Unknown'}
          </div>
          <div
            style={{
              marginBottom: 12,
              padding: '10px 12px',
              borderRadius: 8,
              background: isDark ? '#1f2937' : '#fff7ed',
              border: `1px solid ${isDark ? '#9a3412' : '#fed7aa'}`,
              color: isDark ? '#fdba74' : '#9a3412'
            }}
          >
            <strong>Difference days to reduce:</strong> {mismatchData?.differenceDays ?? 'N/A'}
          </div>
          <div
            style={{
              marginBottom: 12,
              padding: '10px 12px',
              borderRadius: 8,
              background: isDark ? '#052e16' : '#ecfdf5',
              border: `1px solid ${isDark ? '#166534' : '#a7f3d0'}`,
              color: isDark ? '#86efac' : '#065f46'
            }}
          >
            <strong>Requested days:</strong> {mismatchRequestedDays}
          </div>
          <div style={{ marginBottom: 16, color: isDark ? '#cbd5e1' : '#374151' }}>
            {mismatchData?.message || 'The employee leave plan must be updated to match current pending balance.'}
          </div>
          <div style={{ marginBottom: 16, color: isDark ? '#cbd5e1' : '#374151' }}>
            The employee has to update the current vacation planning. The plan should be decreased by{' '}
            <strong>{mismatchData?.differenceDays ?? 'N/A'}</strong> day(s). The employee can choose which annual leave period to update.
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #cbd5e1', padding: '8px 6px', background: isDark ? '#0f172a' : '#eff6ff', color: isDark ? '#93c5fd' : '#1e3a8a' }}>Request ID</th>
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #cbd5e1', padding: '8px 6px', background: isDark ? '#0f172a' : '#eff6ff', color: isDark ? '#93c5fd' : '#1e3a8a' }}>Start</th>
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #cbd5e1', padding: '8px 6px', background: isDark ? '#0f172a' : '#eff6ff', color: isDark ? '#93c5fd' : '#1e3a8a' }}>End</th>
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #cbd5e1', padding: '8px 6px', background: isDark ? '#0f172a' : '#eff6ff', color: isDark ? '#93c5fd' : '#1e3a8a' }}>Days</th>
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #cbd5e1', padding: '8px 6px', background: isDark ? '#0f172a' : '#eff6ff', color: isDark ? '#93c5fd' : '#1e3a8a' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {(mismatchData?.annualLeaves || []).length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: '10px 6px', color: isDark ? '#94a3b8' : '#6b7280' }}>
                      No annual leave requests found for this employee.
                    </td>
                  </tr>
                ) : (
                  (mismatchData?.annualLeaves || []).map(leave => (
                    <tr key={leave.requestId}>
                      <td style={{ borderBottom: '1px solid #e2e8f0', padding: '8px 6px' }}>{leave.requestId}</td>
                      <td style={{ borderBottom: '1px solid #e2e8f0', padding: '8px 6px' }}>{formatDate(leave.startDate)}</td>
                      <td style={{ borderBottom: '1px solid #e2e8f0', padding: '8px 6px' }}>{formatDate(leave.endDate)}</td>
                      <td style={{ borderBottom: '1px solid #e2e8f0', padding: '8px 6px' }}>
                        {calculateBusinessDays(leave.startDate, leave.endDate)}
                      </td>
                      <td style={{ borderBottom: '1px solid #e2e8f0', padding: '8px 6px' }}>{leave.status}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={exportMismatchPdf} variant="outlined">
            Export PDF
          </Button>
          <Button onClick={handleCloseMismatchDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Embedded CSS */}
      <style jsx>{`
        :root {
          --oc-gray-0: #f8f9fa; --oc-gray-1: #f1f3f5; --oc-gray-2: #e9ecef; --oc-gray-3: #dee2e6;
          --oc-gray-4: #ced4da; --oc-gray-5: #adb5bd; --oc-gray-6: #868e96; --oc-gray-7: #495057;
          --oc-gray-8: #343a40; --oc-gray-9: #212529;
          --oc-blue-0: #e7f5ff; --oc-blue-1: #d0ebff; --oc-blue-5: #339af0; --oc-blue-6: #228be6;
          --oc-blue-8: #1971c2; --oc-blue-9: #1864ab;
          --oc-red-0: #fff5f5; --oc-red-1: #ffe3e3; --oc-red-3: #ffa8a8; --oc-red-6: #fa5252;
          --oc-red-8: #e03131; --oc-red-9: #c92a2a;
          --oc-green-0: #ebfbee; --oc-green-1: #d3f9d8; --oc-green-3: #8ce99a; --oc-green-6: #40c057;
          --oc-green-9: #2b8a3e;
          --oc-violet-1: #e5dbff; --oc-violet-9: #5f3dc4;
          --oc-orange-1: #ffe8cc; --oc-orange-9: #d9480f;
          /* Open Colors - Red */
          --oc-red-0: #fff5f5;
          --oc-red-1: #ffe3e3;
          --oc-red-2: #ffc9c9;
          --oc-red-3: #ffa8a8;
          --oc-red-4: #ff8787;
          --oc-red-5: #ff6b6b;
          --oc-red-6: #fa5252;
          --oc-red-7: #f03e3e;
          --oc-red-8: #e03131;
          --oc-red-9: #c92a2a;

          /* Open Colors - Green */
          --oc-green-0: #ebfbee;
          --oc-green-1: #d3f9d8;
          --oc-green-2: #b2f2bb;
          --oc-green-3: #8ce99a;
          --oc-green-4: #69db7c;
          --oc-green-5: #51cf66;
          --oc-green-6: #40c057;
          --oc-green-7: #37b24d;
          --oc-green-8: #2f9e44;
          --oc-green-9: #2b8a3e;
        }

        .Leaves-requests-container {
          container-type: inline-size;
          background: var(--oc-gray-0);
          border-radius: 16px;
          padding: 32px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          margin: 20px;
        }

        .Leaves-requests-title {
          font-size: 28px;
          font-weight: 700;
          color: var(--oc-gray-9);
          margin: 0 0 20px 0;
          letter-spacing: -0.5px;
        }

        .Leaves-requests-filters {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: center;
          background: transparent;
          padding: 20px;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
          margin-bottom: 24px;
        }

        .Leaves-filter-select {
          padding: 10px 16px;
          border: 1.5px solid var(--oc-gray-3);
          border-radius: 8px;
          background: #ffffff;
          color: #343a40;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 160px;
          max-width: 200px;
          color-scheme: light;
        }

        .Leaves-filter-select:hover {
          border-color: var(--oc-blue-5);
          box-shadow: 0 0 0 3px var(--oc-blue-0);
        }

        .Leaves-filter-select:focus {
          outline: none;
          border-color: var(--oc-blue-6);
          box-shadow: 0 0 0 3px var(--oc-blue-1);
        }

        .Leaves-search-container {
          position: relative;
          flex: 1;
          min-width: 200px;
          max-width: 300px;
        }

        .Leaves-search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--oc-gray-5);
          pointer-events: none;
        }

        .Leaves-search-input {
          width: 100%;
          padding: 10px 16px 10px 44px;
          border: 1.5px solid var(--oc-gray-3);
          border-radius: 8px;
          background: white;
          color: var(--oc-gray-8);
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .Leaves-search-input::placeholder { color: var(--oc-gray-5); }
        .Leaves-search-input:hover { border-color: var(--oc-blue-5); box-shadow: 0 0 0 3px var(--oc-blue-0); }
        .Leaves-search-input:focus { outline: none; border-color: var(--oc-blue-6); box-shadow: 0 0 0 3px var(--oc-blue-1); }

        .Leaves-table-wrapper {
          container-type: inline-size;
          background: transparent;
          border-radius: 12px;
          // overflow-x: auto;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
        }

        .Leaves-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }

        .Leaves-table-head {
          background: #4A7BA7;
        }

        .Leaves-table-header {
          padding: 12px 14px;
          text-align: left;
          font-size: 13px;
          font-weight: 700;
          color: white;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          white-space: nowrap;
        }

        .Leaves-table-row {
          transition: all 0.2s ease;
          border-bottom: 1px solid var(--oc-gray-2);
        }

        .Leaves-table-row:hover {
          background: var(--oc-blue-0);
          transform: translateX(2px);
        }

        .Leaves-table-cell {
          padding: 12px 14px;
          color: var(--oc-gray-8);
          font-size: 14px;
          font-weight: 500;
          vertical-align: middle;
        }

        .Leaves-employee-name {
          margin: 0;
          font-weight: 600;
          color: var(--oc-gray-9);
          font-size: 15px;
        }
        /* Custom Buttons */
        .Leaves-btn {
          padding: 8px 8px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .Leaves-btn-save {
          background: linear-gradient(135deg, var(--oc-green-5) 0%, var(--oc-green-6) 100%);
          color: white;
          box-shadow: 0 2px 4px rgba(64, 192, 87, 0.2);
        }

        .Leaves-btn-save:hover {
          background: linear-gradient(135deg, var(--oc-green-6) 0%, var(--oc-green-7) 100%);
          box-shadow: 0 4px 8px rgba(64, 192, 87, 0.3);
          transform: translateY(-1px);
        }

        .Leaves-btn-delete {
          background: linear-gradient(135deg, var(--oc-red-5) 0%, var(--oc-red-6) 100%);
          color: white;
          box-shadow: 0 2px 4px rgba(250, 82, 82, 0.2);
        }

        .Leaves-btn-delete:hover {
          background: linear-gradient(135deg, var(--oc-red-6) 0%, var(--oc-red-7) 100%);
          box-shadow: 0 4px 8px rgba(250, 82, 82, 0.3);
          transform: translateY(-1px);
        }
        .Leaves-status-badge {
          padding: 5px 12px;
          border-radius: 16px;
          font-size: 13px;
          font-weight: 600;
          background: var(--oc-gray-1);
          color: var(--oc-gray-7);
          border: 1px solid var(--oc-gray-3);
        }

        .Leaves-actions {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .MuiAlert-root {
          border-radius: 12px !important;
          font-weight: 500 !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important;
        }

        .MuiAlert-standardSuccess {
          background: linear-gradient(135deg, var(--oc-green-0) 0%, var(--oc-green-1) 100%) !important;
          color: var(--oc-green-9) !important;
          border: 1.5px solid var(--oc-green-3) !important;
        }

        .MuiAlert-standardError {
          background: linear-gradient(135deg, var(--oc-red-0) 0%, var(--oc-red-1) 100%) !important;
          color: var(--oc-red-9) !important;
          border: 1.5px solid var(--oc-red-3) !important;
        }

        /* ========================================================
           Column Priorities (1 = lowest, 5 = highest)
           ======================================================== */

        /* Container Queries (Directly adapts to component container width) */
        @container (max-width: 1280px) {
          .col-priority-1 {
            display: none !important;
          }
        }

        @container (max-width: 1050px) {
          .col-priority-2 {
            display: none !important;
          }
        }

        @container (max-width: 880px) {
          .col-priority-3 {
            display: none !important;
          }
        }

        @container (max-width: 640px) {
          .col-priority-4 {
            display: none !important;
          }
        }

        /* Viewport Media Queries */
        @media (max-width: 1280px) {
          .col-priority-1 {
            display: none !important;
          }
        }

        @media (max-width: 1200px) {
          .Leaves-requests-filters {
            flex-direction: column;
            align-items: stretch;
          }
          .Leaves-filter-select,
          .Leaves-search-container {
            width: 100%;
            max-width: none;
          }
          .Leaves-table-header,
          .Leaves-table-cell {
            padding: 10px 12px;
            font-size: 13px;
          }
        }

        @media (max-width: 1050px) {
          .col-priority-2 {
            display: none !important;
          }
        }

        @media (max-width: 880px) {
          .col-priority-3 {
            display: none !important;
          }
        }

        @media (max-width: 640px) {
          .col-priority-4 {
            display: none !important;
          }
        }

        @media (max-width: 768px) {
          .Leaves-requests-container {
            padding: 16px 12px;
            margin: 10px;
            border-radius: 12px;
          }
          .Leaves-requests-title {
            font-size: 22px;
          }
          .Leaves-table-header,
          .Leaves-table-cell {
            padding: 8px 10px;
            font-size: 12px;
          }
        }

        @media (max-width: 480px) {
          .Leaves-requests-container {
            padding: 12px 6px;
            margin: 4px;
          }
          .Leaves-table-header,
          .Leaves-table-cell {
            padding: 6px 8px;
            font-size: 11px;
          }
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .Leaves-table-row { animation: slideIn 0.3s ease; }
      `}</style>
    </div>
  );
};

export default Requests;
